import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { cloneDeep, get, inRange, isEmpty, isNil, merge, omit } from 'lodash';
import cx from 'classnames';
import ValidatedForm from '../../../../../../common/components/ValidatedForm';
import TextField from '../../../../../../common/components/c_TextField';
import TextArea from '../../../../../../common/components/c_TextArea';
import TextInputList from '../../../../../../common/components/c_TextInputList';
import MaskedTextField from '../../../../../../common/components/c_MaskedTextField';
import DateInputDropDown from '../../../../../../common/components/DateInputDropDown';
import ToggleButton from '../../../../../../common/components/c_ToggleButton';
import DropDownMenu from '../../../../../../common/components/c_DropDownMenu';
import FlatButton from '../../../../../../common/components/c_FlatButton';
import ModalX from '../../../../../../common/svgs/ModalX';
import {validate} from '../../../../../../lib/validation/validator.js';

@connect(store => ({
  labels: get(store.labels, 'Application', {}),
}), null, null, {withRef: true})
export default class Experience extends ValidatedForm {
  static propTypes = {
    experience: PropTypes.object.isRequired,
    model: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    removeListItem: PropTypes.func.isRequired,
    staticData: PropTypes.object.isRequired,
    updateTempState: PropTypes.func.isRequired,
    validateUpdate: PropTypes.func.isRequired,
    validation: PropTypes.object.isRequired,
    showRemove: PropTypes.bool
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      clientId: props.experience.clientId,
      validation: Object.assign(
        this.CustomValidator,
        omit(cloneDeep(props.validation),
          ['contactEmployerReason',
           'current',
           'jobSpecifics',
           'supervisorName',
           'supervisorEmail',
            'supervisorPhoneNumber',
           'reasonForLeaving'
          ])
      )
    };
  }

  // Override ValidatedForm.formModel method
  get formModel() {
    return merge({}, get(this, 'props.experience'), get(this, `props.tempState.workExperiences[${this.props.experience.clientId}]`));
  }

  get CustomValidator() {
    const fieldIsRequired = 'This field is required.';
    const invalidFormat = 'This value is invalid.';
    const component = this;
    const mayContactSupervisor = (val) => isNil(get(component.formModel, 'contactEmployer')) || !get(component.formModel, 'contactEmployer') || val;
    const mayNotContactSupervisor = (val) => isNil(get(component.formModel, 'contactEmployer')) || get(component.formModel, 'contactEmployer') || val;
    const isNotCurrentJob = (val) => (!isNil(get(component.formModel, 'current')) && get(component.formModel, 'current')) || val;
    const isValidPhone = val => !(get(component.formModel, 'contactEmployer')) || val.match(/^[2-9]\d{9}$/);
    const isValidEmail = val => !(get(component.formModel, 'contactEmployer')) || !val || validate(val, 'isEmail');
    const CustomValidator = function() {
      this.contactEmployerReason = {
        custom: {
          func: mayNotContactSupervisor,
          message: fieldIsRequired
        }
      },
      this.employmentEnd = {
        custom: {
          func: (val) => !isNil(get(component.formModel, 'current')) && get(component.formModel, 'current') || val,
          message: fieldIsRequired
        }
      };
      this.supervisorName = {
        custom : {
          func: mayContactSupervisor,
          message: fieldIsRequired
        }
      },
      this.supervisorEmail = {
        custom : {
          func: isValidEmail,
          message: invalidFormat
        }
      },
      this.supervisorPhoneNumber = {
        custom : {
          func: isValidPhone,
          message: 'Must be valid, 10 digits.'
        }
      },
      this.reasonForLeaving = {
        custom : {
          func: isNotCurrentJob,
          message : fieldIsRequired
        }
      },
      this.jobSpecifics = {
        custom : {
          func: (val) => {
            return !isEmpty(val) && inRange(val.filter(text => !isEmpty(text)).length, 3, 9);
          },
          message: 'Please provide between 3 and 8 job specifics.'
        }
      };
    };

    return new CustomValidator();
  }

  handleIsCurrentJob(clientId, val, event) {
    const resetEnd = () => this.props.onChange(clientId, 'employmentEnd', event, null);

    this.props.onChange(clientId, 'current', event, val);
    if (val) {
      setTimeout(resetEnd);
    }
  }

// clear supervisor info when contact employer permission is revoked and reason when permission is granted
  handleContactEmployer(clientId, newValue, event) {
    this.props.onChange(clientId, 'contactEmployer', event, newValue);

    if (newValue === false)
    {
      // clear supervisor info when contact employer permission is revoked and
      setTimeout(() => {
        this.props.onChange(clientId, 'supervisorPhoneNumber', event, null);
        this.props.onChange(clientId, 'supervisorName', event, null);
        this.props.onChange(clientId, 'supervisorEmail', event, null);
      });
    } else {
      // clear reason when permission is granted
      setTimeout(() => {
        this.props.onChange(clientId, 'contactEmployerReason', event, null);
      });
    }
  }

  handleUpdate = (clientId, prop) => {
    return (model, field, event, value) => this.props.onChange(clientId, prop, event, value);
  }

  handleRemove = (clientId) =>
    () => this.props.removeListItem(clientId)

  handleCancel = (clientId) =>
    () => this.props.handleCancelExperience(clientId)

  handleSave = (clientId) =>
    () => {
      // trigger blur event to force validation errors
      for (const refKey in this.refs) {
        if (this.refs[refKey].checkErrors) {
          this.refs[refKey].checkErrors();
        }
      }
      return this.props.saveListItem(clientId);
    }

  renderAttributes(prop, label, updateOn = 'onBlur', valueOn = 'defaultValue') {
    return {
      // need to replace '\n' with actual newline
      floatingLabelText: label,
      ref: label,
      [valueOn]: (this.props.experience[prop]),
      isDirty: !!get(this.state, `validation.${prop}.errors[0]`, false),
      errorText: get(this.state, `validation.${prop}.errors[0]`),
      [updateOn]: this.validateUpdate(this.props.model, prop, this.handleUpdate(this.formModel.clientId, prop))
    };
  }

  renderMenuItems(items) {
    return {
      menuItems: typeof items === 'string' ? this.props.staticData[items] : items
    };
  }

  render() {
    // held here to avoid making a bunch of constants with the same text
    const exampleJobSummaryText = this.props.submitted ? '' : get(this.props.labels, 'jobSummaryExample');
    const jobSummaryDescriptionHead = 'Job Summary';
    const jobSummaryDescription = this.props.submitted ? '' : 'Please provide an overview (up to 400 characters) of the position you held and any special areas of focus.';

    // job responsibilities example text
    const jobResponsibilitiesDescriptionHead = 'Job Specifics';
    const jobResponsibilitiesDescription = this.props.submitted ? '' : 'Please list the specific day to day job responsibilities and/or job functions. At least three are required with a maximum of eight.';
    const exampleJobResponsibilities = this.props.submitted ? [] : get(this.props.labels, 'jobSpecificsExamples');

    const jobDescritionBottom = 'Please remember to proofread before you submit.';

    const contactSupervisor = [{value:true, primaryText:'Yes', subElement:'supervisorContact'},
                               {value:false, primaryText:'No', subElement:'contactEmployerReason'}];

    const index = this.props.index;
    const experience = this.props.experience;

    return (
      <div className={cx('work-experience-wrapper', {'applicant-view': !this.props.adminView})}>
        {
          !this.props.adminView
          &&
          <ModalX
            onClick={this.handleCancel(this.props.experience.clientId)}
            style={{fill: '#000000'}}
          />
        }
        <div
          className='work-experience form-contents grid'
          key={index}
        >
        <div className='row'>
          <div className='col'>
            <TextField
              data-id={'Employer Name'}
              floatingLabelFixed
              {...this.renderAttributes('employerName', 'Employer Name')}
            />
          </div>
          <div className='col'>
            <TextField
              data-id={'City'}
              floatingLabelFixed
              {...this.renderAttributes('city', 'City')}
            />
          </div>
          <div className='col'>
            <DropDownMenu
              canAutoPosition='true'
              data-id={'State'}
              {...this.renderAttributes('state', 'State', 'onChange', 'value')}
              {...this.renderMenuItems('stateList')}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <DropDownMenu
              className='typeofbusiness'
              canAutoPosition='true'
              data-id={'Type of Business'}
              {...this.renderAttributes('typeOfBusiness', get(this.props.labels, 'typeOfBusiness', ''), 'onChange')}
              {...this.renderMenuItems('industryTypesList')}
            />
          </div>
          <div className='col'>
            <TextField
              data-id={'Position/Title'}
              floatingLabelFixed
              maxLength={60}
              {...this.renderAttributes('title', 'Position/Title')}
            />
          </div>
          <div className='col'>
            <TextField
              floatingLabelFixed
              maxLength={25}
              data-id={'Department'}
              {...this.renderAttributes('department', 'Department')}
            />
          </div>
        </div>
        {
          (experience.typeOfBusiness === 12) // Other
          &&
          <div className='row'>
            <div className='col'>
              <TextField
                floatingLabelFixed
                data-id={'Type of Business Other'}
                {...({validation: {required: { message : 'This field is required'}}})}
                {...this.renderAttributes('typeOfBusinessOther', 'If other, please specify')}
              />
            </div>
            <div className='col'>
            </div>
            <div className='col'>
            </div>
          </div>
        }
        <div className='row'>
          <div className='col'>
            <DateInputDropDown
              data-id={'Employed From'}
              dateFormat={'MonthYear'}
              hintText={'12/1999'}
              autoOk
              {...this.renderAttributes('employmentStart', 'Employed From', 'onChange')}
              underlineStyle={{margin: '-1px 0px'}}
              iconStyle={{right: '0px', fill: 'rgb(33, 35, 38)'}}
              labelStyle={{paddingLeft: '0px'}}
            />
          </div>
          <div className='col'>
              <DateInputDropDown
                data-id={'Employed To'}
                dateFormat={'MonthYear'}
                hintText={'01/2001'}
                disabled={experience.current}
                autoOk
                minDate={experience.employmentStart}
                {...this.renderAttributes('employmentEnd', 'Employed To', 'onChange')}
                underlineStyle={{margin: '-1px 0px'}}
                iconStyle={{right: '0px', fill: 'rgb(33, 35, 38)'}}
                labelStyle={{paddingLeft: '0px'}}
                value={experience.current ? null : (experience.employmentEnd || null)}
              />
          </div>
          <div className='col'>
            <FlatButton
              data-id={'Current Job'}
              className={cx('current-job', {active: experience.current})}
              label='Current Job'
              onClick={this.handleIsCurrentJob.bind(this, experience.clientId, !experience.current)}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col'>
              <TextArea
                key={`Reason for Leaving ${experience.current}`}
                data-id={'Reason for Leaving'}
                className='reason-for-leaving'
                description='Reason for Leaving'
                showCharacterCount
                capitalizedSentences
                maxLength={255}
                style={{minHeight: '95px'}}
                disabled={experience.current}
                {...this.renderAttributes('reasonForLeaving', 'Reason for Leaving')}
                defaultValue={experience.current ? '' : this.props.experience.reasonForLeaving}
              />
          </div>
          <div className='col'>
            <ToggleButton
              data-id={'Permission to Contact Employer'}
              menuItems={contactSupervisor}
              floatingLabelText={'Permission to Contact Employer'}
              ref={'Permission to Contact Employer'}
              value={this.props.experience.contactEmployer}
              isDirty={!!get(this.state, 'validation.contactEmployer.errors[0]', false)}
              errorText={get(this.state, 'validation.contactEmployer.errors[0]')}
              onChange={this.handleContactEmployer.bind(this, experience.clientId, !experience.contactEmployer)}
            />
          </div>
          <div className='col'>
            {
              (!experience.contactEmployer)
              &&
            <TextArea
              className='no-contact-reason'
              description='If no, please explain'
              data-id={'If no, please explain'}
              showCharacterCount
              capitalizedSentences
              maxLength={255}
              style={{minHeight: '95px'}}
              {...this.renderAttributes('contactEmployerReason', 'If no, please explain')}
            />
          }
          </div>
        </div>
        {
          (experience.contactEmployer)
          &&
          <div className='row'>
            <div className='col'>
              <TextField
                data-id={`Supervisor's Name`}
                floatingLabelFixed
                disabled={this.props.adminView}
                {...this.renderAttributes('supervisorName', "Supervisor's Name")}
              />
            </div>
            <div className='col'>
              <MaskedTextField
                data-id={`Supervisor's Phone Number`}
                floatingLabelFixed
                mask='(999) 999-9999'
                hideMaskChar
                disabled={this.props.adminView}
                {...this.renderAttributes('supervisorPhoneNumber', "Supervisor's Phone Number")}
              />
            </div>
            <div className='col'>
              <TextField
                data-id={`Supervisor's Email Address`}
                floatingLabelFixed
                disabled={this.props.adminView}
                {...this.renderAttributes('supervisorEmail', "Supervisor's Email")}
              />
            </div>
          </div>
        }
        <div className='row'>
          <div className='col grow-3'>
            <TextArea
              style={{minHeight: '95px'}}
              data-id={jobSummaryDescriptionHead}
              showCharacterCount
              maxLength={500}
              capitalizedSentences
              descriptionHead={jobSummaryDescriptionHead}
              description={jobSummaryDescription}
              descriptionBottom={jobDescritionBottom}
              exampleText={exampleJobSummaryText}
              {...this.renderAttributes('jobSummary')}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col grow-3'>
            <TextInputList
              minLength={3}
              maxLength={8}
              requiredLength={3}
              maxCharLength={250}
              capitalizedSentences
              descriptionHead={jobResponsibilitiesDescriptionHead}
              description={jobResponsibilitiesDescription}
              descriptionBottom={jobDescritionBottom}
              exampleTextArr={exampleJobResponsibilities}
              {...this.renderAttributes('jobSpecifics', 'Job Specifics', 'onBlur', 'value')}
            />
          </div>
        </div>
        {
          !this.props.adminView
          &&
        <div className='row actions'>
          <div className='col'>
            {
              !this.props.experience.isNew
              &&
              <FlatButton className='delete-btn'
                          label='Delete'
                          onClick={this.handleRemove(this.props.experience.clientId)}
                          style={{
                            position: 'absolute',
                            display: 'inline-block',
                            width: '268px',
                            paddingLeft: '30px',
                            paddingRight: '30px',
                            backgroundColor: '#d0011b',
                            border: '1px solid #d0011b',
                            color: '#ffffff',
                            marginTop: '20px',
                          }}
                          hoverStyle={{
                            border: '1px solid #d0011b',
                            color: '#ffffff'
                          }}
                          />
              }
          </div>
          <div className='col grow-2'>
            <div className='row'>
              <div className='col'>
                {
                  this.props.showErrors
                  &&
                  <div className='error-message'>Please fill out the required fields above.</div>
                }
              </div>
              <div className='col grow-2 right-bottons'>
                <FlatButton className='cancel-btn'
                            label='Cancel'
                            onClick={this.handleCancel(this.props.experience.clientId)}
                            style={{
                              display: 'inline-block',
                              width: '165px',
                              paddingLeft: '30px',
                              paddingRight: '30px',
                              marginRight: '25px',
                            }}/>
                <FlatButton className='save-btn'
                            label='Save'
                            onClick={this.handleSave(this.props.experience.clientId)}
                            style={{
                              display: 'inline-block',
                              width: '165px',
                              paddingLeft: '30px',
                              paddingRight: '30px',
                              backgroundColor: '#2eb0e3',
                              boxShadow: '0 2px 1px 0 rgba(0,0,0,.41), 0 0 2px 0 rgba(0,0,0,.12)',
                              borderRadius: '2px',
                              border: '0',
                              color: '#ffffff',
                            }}
                            hoverStyle={{
                              color: '#ffffff'
                            }}
                  />
              </div>
            </div>
          </div>
        </div>
      }
      </div>
    </div>
    );
  }
}



// WEBPACK FOOTER //
// ./shared/react/r_ApplicantDashboard/f_Application/r_ApplicationForm/Experience/c_WorkExperience/Experience.js